import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import format from 'date-fns/format';

import ZRMUtils from '@zg-rentals/zrm-utils-js/lib/utils';
import { Tag } from '@zillow/zrs-comet';

import {
    AppSelectors, RoutingSelectors, PropertiesSelectors,
} from 'app/shared/selectors';
import { STATUS_KEY, getTagTypeByKey } from 'app/shared/utils/statusTagUtils';
import AnalyticsConstants from 'app/shared/constants/AnalyticsConstants';
import accessUtils from 'app/shared/utils/accessUtils';
import analyticsUtils from 'app/client/utils/analyticsUtils';
import ModalActions from 'app/shared/actions/ModalActions';
import ModalConstants from 'app/shared/constants/ModalConstants';
import PaidInclusionConstants from 'app/shared/constants/PaidInclusionConstants';
import PostingActions from 'app/shared/actions/PostingActions';
import PropertiesActions from 'app/shared/actions/PropertiesActions';
import PropertyConstants from 'app/shared/constants/PropertyConstants';
import zGuaranteeIcon from 'app/shared/images/z-guarantee.svg';
import useVisibility from 'app/shared/hooks/useVisibility';

import AdditionalInformation from './components/AdditionalInformation';
import FeedConnectAdditionalInformation from './components/FeedConnectAdditionalInformation';
import ErrorMessage from './components/ErrorMessage';
import LearnMore from './components/LearnMore';

import {
    StickyListingFooterContainer,
    StyledButton,
    StyledContainer,
    StyledFlexContainer,
    StyledGuaranteeIcon,
    StyledMiddleFlex,
    StyledTagSpacer,
} from './StyledComponents';

const {
    makeAppConfig,
    makeCookieEnabledServices,
    makeIsScreenConstellationMobile,
    makeIsScreenConstellationTablet,
} = AppSelectors;
const { SYNDICATION_OVERVIEW_STATUS, LISTING_STATUS } = PropertyConstants;
const { GA_EVENTS, SUFFIX } = AnalyticsConstants;
const { appendSuffix, track } = analyticsUtils;
const { DEACTIVATE_OPTION } = PaidInclusionConstants;

const propTypes = {
    canAccessPayments: PropTypes.bool.isRequired,
    formData: PropTypes.object.isRequired,
    hasRestrictionErrorMessage: PropTypes.bool.isRequired,
    onPublishClick: PropTypes.func.isRequired,
    onNextClick: PropTypes.func.isRequired,
    paidListingExists: PropTypes.bool,
    updateFormData: PropTypes.func.isRequired,
};

const defaultProps = {
    paidListingExists: false,
};

const ConnectedListingFooter = ({
    canAccessPayments,
    formData: {
        active,
        aliasEncoded,
        expirationDate,
        isActivationBlocked,
        isFeed,
        isCommunity,
        isPaidMultifamily,
        singleFamilyContract,
        seattleHousingConnectorStatus,
        // TODO: stop using non form data in formData; prefer current property
        paidInclusion: {
            isEnabled: isInPaidMarket = null,
            isPaymentRequired = null,
            price = null,
            isFlatFeeEnabled,
            isPaymentValid,
        } = {},
        propertyType,
        syndication,
    },
    hasRestrictionErrorMessage,
    onPublishClick,
    onNextClick,
    paidListingExists,
    updateFormData,
}) => {
    const dispatch = useDispatch();
    const appConfig = useSelector(state => makeAppConfig(state));
    const cookieEnabledServices = useSelector(state => makeCookieEnabledServices(state));
    const isScreenMobile = useSelector(state => makeIsScreenConstellationMobile(state));
    const isScreenTablet = useSelector(state => makeIsScreenConstellationTablet(state));
    const currentProperty = useSelector(state => PropertiesSelectors.makeCurrentProperty(state));

    const {
        canUpdateActiveStatus,
        leadGuarantee: {
            isEligible: isEligibleForLeadGuarantee,
        },
        rentGuarantee: {
            isEligible: isEligibleForRentGuarantee,
            isEnabled: isEnabledForRentGuarantee,
        },
    } = useSelector(state => PropertiesSelectors.makeCurrentProperty(state));

    const search = useSelector(state => RoutingSelectors.makeSearch(state));

    const [isVisible, isVisibleRef] = useVisibility(-50, 200);

    useEffect(() => {
        checkQueryParamActions();
    }, []);

    // feature access
    const isFree = isFeed ? false : !isPaymentRequired;

    // set can access flat fee to be false if listing is free
    const canAccessFlatFee = isFree
        ? false
        : accessUtils.canAccessFlatFee({ isFlatFeeEnabled, appConfig, cookieEnabledServices });

    const canFeedConnectCreateListing =
        accessUtils.canFeedConnectCreateListing({ appConfig, cookieEnabledServices });

    const canAccessRentGuarantee =
        accessUtils.canAccessRentGuarantee({ cookieEnabledServices });

    const buttonText = active ? 'Deactivate' : 'Publish';
    const { overview: { displayStatus } } = syndication;
    const isDuplicatePaidListingPosted = hasRestrictionErrorMessage && paidListingExists;

    const disablePublishIfDuplicatePaidListingPosted = !isFeed
        && !active
        && isInPaidMarket
        && isDuplicatePaidListingPosted;

    const renderPricingContent = !isActivationBlocked && !disablePublishIfDuplicatePaidListingPosted;
    const showFeedConnectRate = canFeedConnectCreateListing && singleFamilyContract.isRequired;
    const hidePrice = isFeed === true || canAccessFlatFee === true;

    const checkQueryParamActions = () => {
        const { action } = ZRMUtils.queryUtils.parse(search) || {};

        if (action === 'deactivate') {
            handleDeactivateClick();
        }
    };

    const renderGuaranteeIcon = () => (!active && !canAccessFlatFee && isEligibleForLeadGuarantee)
        && <StyledGuaranteeIcon src={zGuaranteeIcon} data-test="StyledGuaranteeIcon" />;

    const dispatchGAEvent = (gaEventObj) => {
        const replaceLabelWithAliasEncoded = gaEventObj.label === 'ListingManagement' &&
                                                gaEventObj.category === 'PostingPath';
        const gaEvent = {
            ...gaEventObj,
            ...(replaceLabelWithAliasEncoded) && { label: aliasEncoded },
            ...(isEligibleForLeadGuarantee) && {
                action: appendSuffix(gaEventObj.action, SUFFIX.LEAD_GUARANTEE),
            },
        };

        dispatch(track.GAEventV2(gaEvent));
    };

    const disableLeadGuaranteeEligibility = () => {
        const { leadGuarantee } = currentProperty;

        dispatch(PropertiesActions.updateCurrentProperty({
            leadGuarantee: {
                ...leadGuarantee,
                isEligible: false,
            },
        }));
    };

    const displayFeedListingDeactivateModal = () => dispatch(
        ModalActions.showModal({
            modalType: ModalConstants.FEED_LISTING_DEACTIVATE_MODAL,
            header: 'Deactivate this listing?',
            // cta button text is updated in EditListingPage via updateFormData
            updateFormData,
            // events
            // TODO: get feed listing specific ga events
            // onOpen: () => dispatchGAEvent(GA_EVENTS.DEACTIVATE_BEGIN),
            // onClose: () => dispatchGAEvent(GA_EVENTS.DEACTIVATE_DISMISSED),
        })
    );

    const deactivateListing = () => {
        const syndicationCopy = {
            ...syndication,
            overview: {
                status: SYNDICATION_OVERVIEW_STATUS.INACTIVE,
                displayStatus: LISTING_STATUS.INACTIVE,
                publishedCount: 0,
            },
        };

        dispatch(PostingActions.property.deactivate(aliasEncoded)).then((result) => {
            if (result.success === true) {
                if (canAccessPayments) {
                    dispatch(ModalActions.showModal({
                        modalType: ModalConstants.POST_DEACTIVATION_MODAL,
                        onOpen: () => dispatchGAEvent(GA_EVENTS.POST_DEACTIVATION_MODAL_DISPLAY),
                        onClose: () => dispatchGAEvent(GA_EVENTS.POST_DEACTIVATION_MODAL_DISMISS),
                        isFluid: true,
                    }));
                }

                if (isEligibleForLeadGuarantee) {
                    disableLeadGuaranteeEligibility();
                }
            }
        });
        dispatch(PropertiesActions.updateCurrentProperty({ active: false }));
        updateFormData({
            active: false,
            syndication: syndicationCopy,
            expirationDateTimestamp: Date.now(),
        });
    };

    const handleDeactivateClick = () => {
        const formattedExpirationDate = format(new Date(expirationDate), 'MMM D, YYYY');

        if (currentProperty.isFeed) {
            return displayFeedListingDeactivateModal();
        }

        if (isFree || currentProperty.paidInclusion.activeProductType === PaidInclusionConstants.PRODUCT_TYPE[2]) {
            return deactivateListing();
        }

        return dispatch(
            ModalActions.showModal({
                modalType: ModalConstants.PAID_INCLUSION_DEACTIVATION_MODAL,
                onCancelClick: () => dispatch(ModalActions.hideModal()),
                onGotItClick: () => dispatch(ModalActions.hideModal()),
                onDeactivate: (when) => {
                    if (when === DEACTIVATE_OPTION.NOW) {
                        dispatch(PropertiesActions.updateCurrentProperty({ autoBill: false }));
                        dispatchGAEvent(GA_EVENTS.DEACTIVATE_NOW);
                        deactivateListing();
                    } else {
                        dispatchGAEvent(GA_EVENTS.DEACTIVATE_SCHEDULE);
                        dispatch(PostingActions.property.autoBill(aliasEncoded));
                    }
                },
                expirationDate: formattedExpirationDate,
                autoBillEnabled: currentProperty.autoBill,
                isEligibleForLeadGuarantee,
                onOpen: () => dispatchGAEvent(GA_EVENTS.DEACTIVATE_BEGIN),
                onClose: () => dispatchGAEvent(GA_EVENTS.DEACTIVATE_DISMISSED),
                header: 'Deactivate listing',
                isFluid: true,
            })
        );
    };

    const handleCTAClick = () => {
        if (active) {
            handleDeactivateClick();
        } else if (canAccessRentGuarantee && isEligibleForRentGuarantee && !isEnabledForRentGuarantee) {
            onNextClick();
        } else {
            onPublishClick({
                isFreeListing: isFree,
                canAccessFlatFee,
            });
        }
    };

    const renderCTAButton = () => {
        const textListing = isScreenMobile ? buttonText : `${buttonText} Listing`;
        const textFlatFee = isScreenMobile ? 'Continue' : 'Continue to payment';
        const shouldShowTextFlatFee = canAccessFlatFee && !active && !isPaymentValid && !showFeedConnectRate;
        const disableBtn = !canUpdateActiveStatus;
        let textLabel;

        if (!active && canAccessRentGuarantee && isEligibleForRentGuarantee && !isEnabledForRentGuarantee) {
            textLabel = 'Next';
        } else if (shouldShowTextFlatFee) {
            textLabel = textFlatFee;
        } else {
            textLabel = textListing;
        }

        return (
            <StyledButton
                data-test="StatusButton"
                buttonType="primary"
                disabled={disableBtn}
                onClick={handleCTAClick}
            >
                {textLabel}
            </StyledButton>
        );
    };

    return (
        <>
            <div ref={isVisibleRef} />
            <StickyListingFooterContainer
                data-test="StickyListingFooterContainer"
                isVisible={isVisible}
            >
                <StyledContainer>
                    <StyledFlexContainer>
                        {renderPricingContent ? (
                            <>
                                <StyledTagSpacer marginRight="xs">
                                    <Tag
                                        appearance={getTagTypeByKey(STATUS_KEY.LISTING_SUMMARY, displayStatus)}
                                    >
                                        {displayStatus}
                                    </Tag>
                                </StyledTagSpacer>
                                {renderGuaranteeIcon()}
                                <StyledMiddleFlex>
                                    <LearnMore
                                        canAccessFlatFee={canAccessFlatFee}
                                        isInPaidMarket={isInPaidMarket}
                                        isSingleFamilyEnterpriseListing={singleFamilyContract.isRequired}
                                        seattleHousingConnectorStatus={seattleHousingConnectorStatus}
                                        syndication={syndication}
                                    />
                                    {showFeedConnectRate ? (
                                        <FeedConnectAdditionalInformation
                                            isMobile={isScreenMobile}
                                            isCommunity={isCommunity}
                                            isContractActive={singleFamilyContract.isActive}
                                            syndication={syndication}
                                            contractDetails={singleFamilyContract.details}
                                        />
                                    ) : (
                                        <AdditionalInformation
                                            active={active}
                                            isFree={isFree}
                                            isInPaidMarket={isInPaidMarket}
                                            isMobile={isScreenMobile}
                                            isPaidMultifamily={isPaidMultifamily}
                                            hidePrice={hidePrice}
                                            isScreenTablet={isScreenTablet}
                                            price={price}
                                            syndication={syndication}
                                            propertyType={propertyType}
                                        />
                                    )}
                                </StyledMiddleFlex>
                            </>
                        ) : (
                            <ErrorMessage
                                isActive={active}
                                // eslint-disable-next-line max-len
                                disablePublishIfDuplicatePaidListingPosted={disablePublishIfDuplicatePaidListingPosted}
                            />
                        )}
                    </StyledFlexContainer>
                    {renderCTAButton()}
                </StyledContainer>
            </StickyListingFooterContainer>
        </>
    );
};

ConnectedListingFooter.propTypes = propTypes;
ConnectedListingFooter.defaultProps = defaultProps;

export default ConnectedListingFooter;
